// Document Library — single page listing every CSV template the user can
// download, plus deep-links to the existing per-domain export dialogs.
// Visible to all admin / adviser users; portal users don't see this route.

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FileText, Download, Search, ArrowRight, FolderOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import BackButton from '@/components/shared/BackButton';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentLibrary() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], clients: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [clientPickerFor, setClientPickerFor] = useState(null); // the item
  const [pickedClient, setPickedClient] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/document-library`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('load failed');
        setData(await res.json());
      } catch (e) {
        logger.warn('DocumentLibrary: load failed', e);
        toast.error('Could not load the document library');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(data.items.map(i => i.category)))],
    [data.items]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.items.filter(i =>
      (category === 'all' || i.category === category) &&
      (!q || i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q))
    );
  }, [data.items, query, category]);

  const groupedByCategory = useMemo(() => {
    const out = {};
    for (const item of filtered) {
      (out[item.category] = out[item.category] || []).push(item);
    }
    return out;
  }, [filtered]);

  const downloadDirect = async (item) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${item.downloadUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.downloadUrl.split('/').pop() || `${item.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${item.title} downloaded`);
    } catch (e) {
      logger.warn('DocumentLibrary: direct download failed', e);
      toast.error('Network error');
    }
  };

  const handleItemAction = (item) => {
    if (item.navigateTo) {
      navigate(item.navigateTo);
      toast.info(item.navigateHint || 'Use the controls on this page to export.');
      return;
    }
    if (item.requiresClient) {
      setClientPickerFor(item);
      setPickedClient('');
      return;
    }
    if (item.downloadUrl) {
      downloadDirect(item);
    }
  };

  const runClientPick = async () => {
    if (!clientPickerFor || !pickedClient) return;
    const item = clientPickerFor;
    if (item.navigateToTemplate) {
      navigate(item.navigateToTemplate.replace('{clientId}', pickedClient));
      toast.info(item.navigateHint || 'Use the controls on this page to export.');
    } else if (item.downloadUrlTemplate) {
      const url = item.downloadUrlTemplate.replace('{clientId}', pickedClient);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { toast.error('Download failed'); return; }
        const blob = await res.blob();
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = `${item.id}_${pickedClient}.csv`;
        a.click();
        URL.revokeObjectURL(u);
        toast.success(`${item.title} downloaded`);
      } catch (e) {
        logger.warn('DocumentLibrary: per-client download failed', e);
        toast.error('Network error');
      }
    }
    setClientPickerFor(null);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-[#5D524D]" data-testid="document-library-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading library…
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 sm:p-8" data-testid="document-library">
      {/* Back nav */}
      <BackButton fallback="/app" className="mb-4" testId="document-library-back" />
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#F5EDE4] border border-[#E6E0D9] flex items-center justify-center">
            <FolderOpen className="w-5 h-5 text-[#A86A3A]" />
          </div>
          <h1 className="text-3xl font-light text-[#2C2420]">Document Library</h1>
        </div>
        <p className="text-sm text-[#5D524D] max-w-2xl">
          Every CSV template and on-demand export the platform offers, in one place. Templates download instantly; exports open the relevant filter dialog so you can pick what's included.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="library-filters">
        <div className="flex items-center gap-2 bg-white border border-[#E6E0D9] rounded-md px-3 h-9 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[#8C8078]" />
          <Input
            placeholder="Search templates…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 px-0 h-7"
            data-testid="library-search"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[200px]" data-testid="library-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map(c => (
              <SelectItem key={c} value={c}>{c === 'all' ? 'All categories' : c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sections */}
      {Object.keys(groupedByCategory).length === 0 ? (
        <div className="text-center text-[#5D524D] py-16 border border-dashed border-[#E6E0D9] rounded-lg" data-testid="library-empty">
          No documents match your search.
        </div>
      ) : (
        Object.entries(groupedByCategory).map(([cat, items]) => (
          <section key={cat} className="mb-8" data-testid={`library-section-${cat.toLowerCase().replace(/ /g, '-')}`}>
            <h2 className="text-xs uppercase tracking-wider text-[#8C8078] mb-3 font-semibold">{cat}</h2>
            <div className="space-y-2">
              {items.map(item => (
                <div
                  key={item.id}
                  className="bg-white border border-[#E6E0D9] rounded-lg p-4 flex items-start gap-4 hover:border-[#A86A3A] transition-colors"
                  data-testid={`library-item-${item.id}`}
                >
                  <div className="w-10 h-10 rounded-md bg-[#F9F7F5] border border-[#E6E0D9] flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-[#5D524D]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-medium text-[#2C2420]">{item.title}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F5F3F0] text-[#5D524D] border border-[#E6E0D9]">{item.scope}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F9F7F5] text-[#8C8078] border border-[#E6E0D9]">{item.format}</span>
                    </div>
                    <p className="text-xs text-[#5D524D]">{item.description}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleItemAction(item)}
                    data-testid={`library-item-action-${item.id}`}
                    className="shrink-0"
                  >
                    {item.navigateTo || item.navigateToTemplate ? (
                      <>Open <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>
                    ) : (
                      <><Download className="w-3.5 h-3.5 mr-1.5" /> {item.requiresClient ? 'Pick client' : 'Download'}</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        ))
      )}

      {/* Client picker dialog (for per-client templates) */}
      <Dialog open={!!clientPickerFor} onOpenChange={(o) => !o && setClientPickerFor(null)}>
        <DialogContent data-testid="library-client-picker" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pick a client</DialogTitle>
            <DialogDescription>
              {clientPickerFor?.title} is per-client — choose which client to {clientPickerFor?.downloadUrlTemplate ? 'download for' : 'jump to'}.
            </DialogDescription>
          </DialogHeader>
          <Select value={pickedClient} onValueChange={setPickedClient}>
            <SelectTrigger data-testid="library-client-picker-select"><SelectValue placeholder="Choose a client…" /></SelectTrigger>
            <SelectContent>
              {data.clients.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setClientPickerFor(null)}>Cancel</Button>
            <Button onClick={runClientPick} disabled={!pickedClient} data-testid="library-client-picker-confirm">
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
